import * as React from 'react';
import { useEffect, useState, useRef } from 'react';

import { FavouriteResultComponentProps } from 'app/area/favourite/types';
import {
  useCaseTableViewEvents,
  useLoadReportLayoutEffect,
  useSelectors
} from 'app/area/case-report/case-table-view/hooks/case-table-view-hooks';
import { useSyncScroll } from 'app/area/case-report/case-table-view/hooks/sync-scroll-hooks';

import { CaseTableViewHeader } from 'app/area/case-report/case-table-view/components/case-table-view-header';
import { CaseTableViewRows } from 'app/area/case-report/case-table-view/components/case-table-view-rows';
import { ElementLoader } from 'ui';
import { Waypoint } from 'react-waypoint';
import { CaseTableMobileView } from 'app/area/case-report/case-table-view/components/case-table-mobile-view';
import { caseTableViewTranslation } from 'app/area/case-report/case-table-view/constants';

import { useCaseListingException } from 'app/area/case-report/components/use-case-listing-exception';
import { useTranslation } from 'app/infrastructure/translate/hooks';
import { STATE } from 'app/constants';
import { caseListingTranslation } from 'app/area/case-report/case-listing/constants';

import { useInstanceOptions } from 'app/area/favourite/favourite-context';
import { CaseListingInfo } from 'app/area/case-report/components/case-listing-no-columns-selected';
import { CaseTableViewContext } from 'app/area/case-report/case-table-view/case-table-view-context';

import { withCallsToActions } from 'app/area/case/features';
import styles from './case-table-view.scss';
import { HeaderPanelProps } from 'app/components';
import { useLocation, useSearchParams } from 'react-router-dom';

interface CaseTableViewContainerProps extends FavouriteResultComponentProps {
  autoExecute: boolean;
  callsToActions?: HeaderPanelProps['callsToActions']; 
  onChangeCaseStatus?: HeaderPanelProps['onChangeCaseStatus'];
  changingCaseStatus?: boolean;
  layoutTypeContextId?: number;
}

export type TranslationType = {
  readonly [P in keyof typeof caseListingTranslation]: string
} & {
  readonly [P in keyof typeof caseTableViewTranslation]: string
};

export const CaseTableViewContainer = withCallsToActions((props: CaseTableViewContainerProps) => {
  const translation = useTranslation(STATE.caseListing, { ...caseListingTranslation, ...caseTableViewTranslation });
  const [resultsLoaded, setResultsLoaded] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const location: ReturnType<typeof useLocation> = useLocation();

  const { autoExecute, workingDefinition, layoutTypeContextId } = props;
  const { execute, onDeleteCase, onCopyRiskEvaluation } = useCaseTableViewEvents(workingDefinition, autoExecute);
  const { totalNumberOfResults, entities, columns, fetching, locale, errorsExecuteFailed, domainAttributes } = useSelectors();
  const tableViewRef = useRef<HTMLDivElement>(null);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { handleScroll, tableWidth, tableColumnWidths } = useSyncScroll({ tableContainerRef, resultsLoaded });
  const canFetchMore = !!entities?.length && (entities?.length < totalNumberOfResults);
  const { height: maxHeight } = useInstanceOptions();
  const isMobilePrint = tableWidth > Math.max(headerContainerRef.current?.clientWidth || 0, 890);
  
  const totalResults = totalNumberOfResults || 0;
  const hasMoreResults = !!entities && entities.length > 0 && entities.length < totalResults;

  useLoadReportLayoutEffect();

  useEffect(() => {
    setResultsLoaded(!fetching);
  }, [fetching]);

  const fetchMoreData = () => {
    if (autoExecute && !fetching && columns?.length) {
      execute(entities?.length);
    }
  };

  const ErrorHandlingComponent = useCaseListingException({
    autoExecute,
    errorsExecuteFailed,
    fetching,
    totalNumberOfResults,
    translation
  });

  if (ErrorHandlingComponent !== null) {
    return ErrorHandlingComponent;
  }

  if (!columns?.length && !!totalNumberOfResults) {
    return <CaseListingInfo message={translation.noColumnsWereSelected} />;
  }

  return (
    <CaseTableViewContext.Provider value={{
      rows: entities,
      columns,
      locale,
      tableContainerRef,
      translation,
      onDeleteCase,
      onCopyRiskEvaluation,
      tableViewRef,
      domainAttributes,
      callsToActions: props.callsToActions,
      onChangeCaseStatus: props.onChangeCaseStatus,
      changingCaseStatus: props.changingCaseStatus,
      searchParams,
      location,
      layoutTypeContextId
    }}>
      <div className={styles.caseViewTableWrapper} ref={tableViewRef}>
        {totalNumberOfResults ? (
          <div className={`${styles.caseViewTableContainer} scrollable`} style={maxHeight ? { maxHeight } : null}>
            <div className={`${styles.tableView} ${isMobilePrint ? styles.hideInPrint : ''}`}>
              <CaseTableViewHeader
                columns={columns}
                tableWidth={tableWidth}
                columnWidths={tableColumnWidths}
                containerRef={headerContainerRef}
                handleScroll={handleScroll}
              />
              <CaseTableViewRows />
            </div>

            <div className={`${styles.cardView} ${isMobilePrint ? styles.mobilePrintLayout : ''}`}>
              <CaseTableMobileView />
            </div>
            {hasMoreResults && !fetching && (
              <>
                <button
                  type="button"
                  data-testid="table-load-more-hint-button"
                  className={`${styles.loadMoreHint} ${styles.hideInPrint}`}
                  onClick={fetchMoreData}
                >
                  {translation.textLoadMoreCases}
                </button>
                <div
                  data-testid="table-load-more-hint-print"
                  className={`${styles.loadMoreHint} ${styles.showInPrint}`}
                >
                  {translation.textMoreCasesExistPrint}
                </div>
              </>
            )}
            <div className={styles.hideInPrint}>
              {canFetchMore && (
                <Waypoint key={`wp-${entities?.length || 0}`} onEnter={fetchMoreData} bottomOffset="-10%" />
              )}
            </div>
          </div>
        ) : null}
        {fetching && <ElementLoader />}
      </div>
    </CaseTableViewContext.Provider>
  );
});
